"use client";

// Lives INSIDE <VideoPlayer> (so it can use Limeplay hooks) and connects the
// Limeplay media store/events to the app-level Zustand player store.

import { useEffect, useRef } from "react";

import {
  useMediaApi,
  useMediaEvents,
} from "@/components/video-player/lib/media-kit";
import { useMediaStore } from "@/hooks/limeplay/use-media";
import { normalizeStart, usePlayerStore } from "@/stores/player-store";

const HEARTBEAT_MS = 30_000;

export default function PlayerBridge() {
  const api = useMediaApi();
  const events = useMediaEvents();
  const mediaElement = useMediaStore((state) => state.mediaElement);
  // true while a source is loading: the browser resets playbackRate to its
  // default then, and that reset must never overwrite the user's speed.
  const loadingRef = useRef(false);

  // 1) store → Limeplay (imperative handles)
  useEffect(() => {
    const s = usePlayerStore.getState();
    s._registerControls({
      play: () => api.getState().playback.play(),
      pause: () => api.getState().playback.pause(),
      seek: (t) => api.getState().timeline.seek(t),
      setRate: (r) => {
        api.getState().playbackRate.setPlaybackRate(r);
        // new sources start at defaultPlaybackRate → keep it in sync
        const m = api.getState().media.mediaElement;
        if (m) m.defaultPlaybackRate = r;
      },
      setVolume: (v) => api.getState().volume.setVolume(v),
    });
    return () => usePlayerStore.getState()._registerControls(null);
  }, [api]);

  // 2) Limeplay events → store
  useEffect(() => {
    const st = () => usePlayerStore.getState();
    const offs: Array<() => void> = [];

    offs.push(
      events.on("assetloadstart", () => {
        loadingRef.current = true;
      }),
      events.on("assetloaderror", () => {
        loadingRef.current = false;
      }),
      events.on("assetloaded", () => {
        const s = st();
        const m = api.getState();
        // restore user prefs on the (single) media element
        const el = m.media.mediaElement;
        if (el) el.defaultPlaybackRate = s.speed;
        m.playbackRate.setPlaybackRate(s.speed);
        m.volume.setVolume(s.volume);
        loadingRef.current = false;

        const start = normalizeStart(s.startAt, m.timeline.duration);
        if (start > 0) m.timeline.seek(start);

        if (s.pendingPlay) {
          m.playback.play().catch(() => {
            // e.g. browser blocked playback → stay paused, show play button
            st()._report({ isPlaying: false, pendingPlay: false, isBuffering: false });
          });
        } else {
          st()._report({ isBuffering: false });
        }
      }),
      events.on("play", () =>
        st()._report({ isPlaying: true, isBuffering: false }),
      ),
      events.on("pause", () => {
        st()._report({ isPlaying: false });
        st()._playbackEvent("pause");
      }),
      events.on("ended", () => st()._ended()),
      events.on("buffering", ({ isBuffering }) =>
        st()._report({ isBuffering }),
      ),
      events.on("timeupdate", ({ currentTime, duration }) =>
        st()._tick(currentTime, duration),
      ),
      events.on("volumechange", ({ volume }) => {
        if (volume !== st().volume) st()._report({ volume });
      }),
    );

    return () => offs.forEach((off) => off());
  }, [api, events]);

  // 2b) speed ← the media element itself. Limeplay's own `ratechange` event
  // reads Shaka's rate, which is always 1 for progressive files that bypass
  // Shaka, so it must not be trusted here.
  useEffect(() => {
    if (!mediaElement) return;
    const onRate = () => {
      if (loadingRef.current) return;
      const rate = mediaElement.playbackRate;
      if (rate > 0 && rate !== usePlayerStore.getState().speed) {
        usePlayerStore.getState()._report({ speed: rate });
      }
    };
    mediaElement.addEventListener("ratechange", onRate);
    return () => mediaElement.removeEventListener("ratechange", onRate);
  }, [mediaElement]);

  // 3) heartbeat (analytics emitted by the store)
  useEffect(() => {
    const id = setInterval(() => {
      if (usePlayerStore.getState().isPlaying) {
        usePlayerStore.getState()._playbackEvent("heartbeat");
      }
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  return null;
}
